import React, { useRef } from "react";
import { Flex, Button, Select, Text } from "@chakra-ui/react";
import { ChevronLeftIcon, ChevronRightIcon } from "@chakra-ui/icons";

interface PaginationProps {
  page: number;
  totalPages: number;
  handlePageChange: (page: number) => void;
}

const Pagination = ({
  page,
  totalPages,
  handlePageChange,
}: PaginationProps) => {
  const pageSelectRef = useRef<HTMLSelectElement>(null);

  const renderPageNumbers = () => {
    const pageNumbers = [];
    const maxVisibleButtons = 9;
    const ellipsis = (
      <Button
        onClick={() => pageSelectRef.current?.focus()}
        cursor="not-allowed"
        w={8}
        h={8}
        borderRadius={8}
      >
        ...
      </Button>
    );

    if (totalPages <= maxVisibleButtons) {
      for (let i = 1; i <= totalPages; i++) {
        pageNumbers.push(renderPageButton(i));
      }
    } else {
      pageNumbers.push(renderPageButton(1));

      if (page > 4) {
        pageNumbers.push(ellipsis);
      }

      const startPage = Math.max(2, page - 2);
      const endPage = Math.min(page + 2, totalPages - 1);

      for (let i = startPage; i <= endPage; i++) {
        pageNumbers.push(renderPageButton(i));
      }

      if (page < totalPages - 3) {
        pageNumbers.push(ellipsis);
      }

      pageNumbers.push(renderPageButton(totalPages));
    }

    return pageNumbers;
  };

  const renderPageButton = (i: number) => (
    <Button
      key={i}
      alignItems="center"
      justifyContent="center"
      w={8}
      h={8}
      borderRadius={8}
      backgroundColor={i === page ? "brand.500" : "gray.200"}
      color={i === page ? "white" : "gray.700"}
      _hover={{
        backgroundColor: i === page ? "brand.600" : "gray.300",
      }}
      onClick={() => handlePageChange(i)}
    >
      {i}
    </Button>
  );

  return (
    <Flex justifyContent="center" mt="4" alignItems="center">
      <Button
        borderRadius={8}
        onClick={() => handlePageChange(page - 1)}
        isDisabled={page === 1}
        mr="2"
        w={8}
        h={8}
      >
        <ChevronLeftIcon />
      </Button>

      <Flex alignItems={"center"} gap={2}>
        {renderPageNumbers()}
      </Flex>

      <Button
        borderRadius={8}
        onClick={() => handlePageChange(page + 1)}
        isDisabled={page === totalPages}
        ml="2"
        w={8}
        h={8}
      >
        <ChevronRightIcon />
      </Button>
      <Flex ml={8} gap={1} alignItems="center">
        <Select
          value={page}
          onChange={(e) => handlePageChange(Number(e.target.value))}
          size="sm"
          width={16}
          ref={pageSelectRef}
        >
          {Array.from({ length: totalPages }, (_, i) => i + 1).map((item) => (
            <option key={item} value={item}>
              {item}
            </option>
          ))}
        </Select>
        <Text fontWeight={"bold"} color="gray.600" width={24}>
          of {totalPages}
        </Text>
      </Flex>
    </Flex>
  );
};

export default Pagination;
